import { Box, type SxProps, type Theme } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { type KeyboardEvent, type ReactNode, useRef } from "react";
import { accessibleIndicatorColor } from "../../../theme/palette";
import { INK, MOTION, NEUTRAL, RADIUS, SURFACE, VISUAL_CHOICE } from "../../../theme/tokens";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";

/** `content` is drawn inside a tile; `swatch` is a CSS background drawn round. */
type OptionVisual = { content: ReactNode; swatch?: never } | { swatch: string; content?: never };

export type VisualChoiceOption<T extends string> = OptionVisual & {
	value: T;
	/** Accessible name and hover tooltip, already translated. */
	label: string;
	dataTestId?: string;
};

export enum VisualChoiceShape {
	Rounded = "rounded",
	Circle = "circle",
}

type GroupLabel = { ariaLabel: string; ariaLabelledBy?: never } | { ariaLabelledBy: string; ariaLabel?: never };

type Props<T extends string> = GroupLabel & {
	options: VisualChoiceOption<T>[];
	/** `null` when none of these options is the current one. */
	value: T | null;
	onChange: (value: T) => void;
	/** Content tiles only; swatches are always `VISUAL_CHOICE.SWATCH` and round. */
	size: keyof typeof VISUAL_CHOICE.TILE;
	shape: VisualChoiceShape;
	// DEFAULT: absent = enabled.
	disabled?: boolean;
	// DEFAULT: absent = no test id on the group.
	dataTestId?: string;
	// DEFAULT: absent = no extra styling.
	sx?: SxProps<Theme>;
};

const NEXT_KEYS = new Set(["ArrowDown", "ArrowRight"]);
const PREVIOUS_KEYS = new Set(["ArrowUp", "ArrowLeft"]);

/** Where an arrow key lands, wrapping at both ends. */
const targetIndex = (key: string, current: number, count: number, isRtl: boolean): number | null => {
	if (key === "Home") return 0;
	if (key === "End") return count - 1;
	const horizontalFlip = isRtl && (key === "ArrowLeft" || key === "ArrowRight");
	if (NEXT_KEYS.has(key)) return (current + (horizontalFlip ? -1 : 1) + count) % count;
	if (PREVIOUS_KEYS.has(key)) return (current + (horizontalFlip ? 1 : -1) + count) % count;
	return null;
};

const tileSx = (theme: Theme, isSelected: boolean, isSwatch: boolean, size: number, shape: VisualChoiceShape) => {
	const accent = accessibleIndicatorColor(theme.palette.primary, SURFACE, INK.PRIMARY);
	const { REST, SELECTED } = VISUAL_CHOICE.SWATCH_RING_PX;
	const selectedHalo = `0 0 0 ${VISUAL_CHOICE.SELECTED_RING_PX}px ${alpha(accent, VISUAL_CHOICE.SELECTED_RING_ALPHA)}`;
	return {
		position: "relative" as const,
		// Flex, not grid: oversized content stays centred instead of growing the track.
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		flexShrink: 0,
		width: size,
		height: size,
		p: 0,
		font: "inherit",
		color: "inherit",
		cursor: "pointer",
		overflow: "hidden",
		borderRadius: isSwatch || shape === VisualChoiceShape.Circle ? "50%" : `${RADIUS.TILE}px`,
		...(isSwatch
			? {
					border: `${VISUAL_CHOICE.SWATCH_GAP_PX}px solid ${SURFACE}`,
					boxShadow: `0 0 0 ${isSelected ? SELECTED : REST}px ${isSelected ? accent : NEUTRAL[200]}`,
				}
			: {
					border: `${VISUAL_CHOICE.TILE_BORDER_PX}px solid ${isSelected ? accent : NEUTRAL[200]}`,
					backgroundColor: isSelected ? SURFACE : NEUTRAL[50],
					boxShadow: isSelected ? selectedHalo : "none",
				}),
		transition: theme.transitions.create(["transform", "border-color", "box-shadow", "background-color"], {
			duration: MOTION.DURATION.CONTROL,
			easing: MOTION.EASE_OUT,
		}),
		"@media (hover: hover) and (pointer: fine)": {
			"&:hover:not(:disabled)": {
				transform: `translateY(-${VISUAL_CHOICE.HOVER_LIFT_PX}px)`,
				...(isSwatch ? {} : { borderColor: isSelected ? accent : alpha(accent, VISUAL_CHOICE.HOVER_BORDER_ALPHA) }),
			},
		},
		"&:active:not(:disabled)": { transform: `scale(${VISUAL_CHOICE.PRESS_SCALE})` },
		"&:focus-visible": { outline: `2px solid ${accent}`, outlineOffset: 2 },
		"&:disabled": { cursor: "default", opacity: VISUAL_CHOICE.DISABLED_OPACITY },
		"@media (prefers-reduced-motion: reduce)": {
			transition: "none",
			"&:hover:not(:disabled), &:active:not(:disabled)": { transform: "none" },
		},
	};
};

/** A single-select radio group of picture tiles or color swatches, for a choice people make by looking. */
export const VisualChoiceGrid = <T extends string>({
	options,
	value,
	onChange,
	size,
	shape,
	disabled,
	dataTestId,
	sx,
	ariaLabel,
	ariaLabelledBy,
}: Props<T>) => {
	const theme = useTheme();
	const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
	const selectedIndex = options.findIndex((option) => option.value === value);
	// DEFAULT: with nothing chosen the first option is the group's tab stop, per the radio-group pattern.
	const tabStopIndex = selectedIndex === -1 ? 0 : selectedIndex;
	const tileSize = VISUAL_CHOICE.TILE[size];

	const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
		// The app flips `body.dir`, not the MUI theme, so read direction from the element.
		const isRtl = getComputedStyle(event.currentTarget).direction === "rtl";
		const next = targetIndex(event.key, index, options.length, isRtl);
		if (next === null) return;
		event.preventDefault();
		optionRefs.current[next]?.focus();
		onChange(options[next].value);
	};

	return (
		<Box
			role="radiogroup"
			aria-label={ariaLabel}
			aria-labelledby={ariaLabelledBy}
			aria-disabled={disabled}
			data-testid={dataTestId}
			sx={[{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", p: 0.5 }, ...(Array.isArray(sx) ? sx : [sx])]}
		>
			{options.map((option, index) => {
				const isSelected = index === selectedIndex;
				const isSwatch = option.swatch !== undefined;
				return (
					<InHouseTooltip key={option.value} title={option.label} disableInteractive>
						<Box
							component="button"
							type="button"
							role="radio"
							aria-checked={isSelected}
							aria-label={option.label}
							tabIndex={index === tabStopIndex ? 0 : -1}
							disabled={disabled}
							data-testid={option.dataTestId}
							data-selected={isSelected}
							ref={(node: HTMLButtonElement | null) => {
								optionRefs.current[index] = node;
							}}
							onClick={() => onChange(option.value)}
							onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => handleKeyDown(event, index)}
							sx={[tileSx(theme, isSelected, isSwatch, isSwatch ? VISUAL_CHOICE.SWATCH : tileSize, shape), isSwatch ? { background: option.swatch } : {}]}
						>
							{option.content}
						</Box>
					</InHouseTooltip>
				);
			})}
		</Box>
	);
};
